/**
 * Client-persisted user preferences for the notification bell / center.
 *
 * There is no backend endpoint for these preferences, so they live in localStorage and are
 * mirrored into shared, SSR-safe `useState` so every component (the bell, the settings page,
 * and useNotifications' incoming handler) reads the same reactive object.
 */
export interface NotificationSettings {
  /** Master switch — when off, incoming notifications are muted (no toast, no badge bump). */
  enabled: boolean;
  /** Show a pop-up toast when a new notification arrives. */
  alerts: boolean;
  /** Receive site-wide announcements (broadcasts). */
  broadcasts: boolean;
}

const STORAGE_KEY = "cld_notification_settings";

const DEFAULTS: NotificationSettings = {
  enabled: true,
  alerts: true,
  broadcasts: true,
};

export function useNotificationSettings() {
  const settings = useState<NotificationSettings>("notification_settings", () => ({ ...DEFAULTS }));

  /** Load persisted settings from localStorage (client-only). Safe to call repeatedly. */
  const load = () => {
    if (!import.meta.client) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<NotificationSettings>;
        settings.value = { ...DEFAULTS, ...parsed };
      }
    } catch (err) {
      console.error("[notification-settings] failed to load:", err);
    }
  };

  /** Persist the current settings to localStorage (client-only). */
  const save = () => {
    if (!import.meta.client) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings.value));
    } catch (err) {
      console.error("[notification-settings] failed to save:", err);
    }
  };

  return { settings, load, save };
}
